/**
 * "Install app" button — surfaces the PWA install prompt.
 * - Chrome/Edge fire `beforeinstallprompt` we capture and use.
 * - On iOS Safari (no prompt), we show a small instructions modal.
 * - When already installed (display-mode: standalone) we hide the button.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";

export default function InstallAppButton({ variant = "header" }) {
  const { t, i18n } = useTranslation();
  const isES = i18n.language === "es";
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) {
      setInstalled(true);
      return;
    }
    function handler(e) {
      e.preventDefault();
      setDeferred(e);
    }
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => setInstalled(true));
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  if (installed) return null;

  function isIos() {
    const ua = window.navigator.userAgent;
    return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  }

  async function onClick() {
    if (deferred) {
      deferred.prompt();
      try {
        await deferred.userChoice;
      } catch {}
      setDeferred(null);
      return;
    }
    if (isIos()) {
      setShowIos(true);
      return;
    }
    // Generic fallback help
    setShowIos(true);
  }

  const label = isES ? "Instalar app" : "Install app";

  if (variant === "card") {
    return (
      <>
        <Button
          onClick={onClick}
          className="btn-cobalt rounded-none h-11 px-5"
          data-testid="install-app-card"
        >
          ↓ {label}
        </Button>
        {showIos && <IosInstructions onClose={() => setShowIos(false)} isES={isES} />}
      </>
    );
  }

  return (
    <>
      <button
        onClick={onClick}
        className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-bold border-2 border-[#1C1917] bg-[#FFC53D] hover:bg-white px-3 h-9 transition-colors shadow-[2px_2px_0_0_#1C1917] hover:shadow-[3px_3px_0_0_#1C1917]"
        data-testid="install-app-header"
        aria-label={label}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <path d="M12 4v12m0 0l-5-5m5 5l5-5M4 20h16" />
        </svg>
        <span>{label}</span>
      </button>
      {showIos && <IosInstructions onClose={() => setShowIos(false)} isES={isES} />}
    </>
  );
}

function IosInstructions({ onClose, isES }) {
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-[#1C1917]/30 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#FDFBF7] border-2 border-[#1C1917] shadow-[8px_8px_0_0_#1C1917] max-w-md p-6"
        data-testid="install-instructions"
      >
        <span className="sticker">{isES ? "Instalar DocuConvert" : "Install DocuConvert"}</span>
        <h3 className="display text-2xl mt-3">
          {isES ? "Instálala en tu dispositivo" : "Install on your device"}
        </h3>
        <ol className="mt-5 grid gap-3 text-sm text-[#1C1917]">
          <li><strong>1.</strong> {isES
            ? "En tu navegador (Chrome, Edge, Safari) busca el botón “Instalar” o el icono de descarga junto a la URL."
            : "In your browser (Chrome, Edge, Safari) look for the “Install” button or the download icon next to the URL."}</li>
          <li><strong>2.</strong> {isES
            ? "Si estás en iPhone/iPad: pulsa el botón “Compartir” y luego “Añadir a pantalla de inicio”."
            : "On iPhone/iPad: tap the “Share” button and then “Add to Home Screen”."}</li>
          <li><strong>3.</strong> {isES
            ? "DocuConvert aparecerá como una app nativa con su propio icono."
            : "DocuConvert will appear as a native app with its own icon."}</li>
        </ol>
        <Button
          onClick={onClose}
          className="btn-primary rounded-none h-11 px-6 mt-6"
          data-testid="install-instructions-close"
        >
          OK
        </Button>
      </div>
    </div>
  );
}
